import { serviceIcon } from './bills'

export type Beneficiary = { name: string; account: string; bank: string; initials: string }

export const BENEFICIARIES: Beneficiary[] = [
  { name: 'Ada Okafor', account: '0803 987 6543', bank: 'GTBank', initials: 'AO' },
  { name: 'Obi Nwosu', account: '0705 221 8890', bank: 'Access Bank', initials: 'ON' },
  { name: 'Ngozi Eze', account: '0812 445 1120', bank: 'Zenith Bank', initials: 'NE' },
  { name: 'Tunde Bello', account: '0906 773 4408', bank: 'Kuda', initials: 'TB' },
]

export const BANKS = ['GTBank', 'Access Bank', 'Zenith Bank', 'UBA', 'First Bank', 'Kuda', 'Opay', 'VE Bank']

export const QUICK_AMOUNTS = [5000, 10000, 25000, 50000]

/** Available balance in kobo-free naira, used for the insufficient-funds guard. */
export const BALANCE = 125680.5

export const TRANSFER_FEE = 0

export function formatAmount(value: number) {
  return value.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export type Payment = {
  isBill: boolean
  bill: string | null
  title: string
  party: string
  account: string
  bank: string
  amount: string
  amountValue: number
  narration: string
  icon: string
}

/**
 * Single source of truth for the payment being prepared. Draft, Review and
 * Success all read the same query parameters so the figures a customer sees
 * never change between steps.
 */
export function readPayment(pathname: string): Payment {
  const params = new URLSearchParams(pathname.split('?')[1] || '')
  const bill = params.get('bill')

  if (bill) {
    const provider = params.get('prov') || 'Biller'
    const target = params.get('target') || '0803 123 4567'
    const plan = params.get('plan') || ''
    const amountValue = Number(params.get('amt')) || 2000
    return {
      isBill: true,
      bill,
      title: `${bill} payment`,
      party: provider,
      account: target,
      bank: `${bill} biller`,
      amount: formatAmount(amountValue),
      amountValue,
      narration: plan || `${bill} payment`,
      icon: serviceIcon(bill),
    }
  }

  const fallback = BENEFICIARIES[0]
  const amountValue = Number(params.get('amt')) || 25000
  return {
    isBill: false,
    bill: null,
    title: 'Transfer draft',
    party: params.get('to') || fallback.name,
    account: params.get('acct') || fallback.account,
    bank: params.get('bank') || fallback.bank,
    amount: formatAmount(amountValue),
    amountValue,
    narration: params.get('note') || 'Rent',
    icon: 'send',
  }
}

export type ParsedRequest = { beneficiary: Beneficiary; amountValue: number; narration: string }

/**
 * Turns a natural-language request into transfer fields. Vee only ever fills
 * the form — the customer still reviews every value and confirms it themselves.
 */
export function parseRequest(text: string): ParsedRequest | null {
  const cleaned = text.replace(/,/g, '')
  const amountMatch = cleaned.match(/(?:₦|ngn\s*)?(\d[\d.]*)\s*(k\b)?/i)
  const beneficiary = BENEFICIARIES.find((candidate) =>
    cleaned.toLowerCase().includes(candidate.name.split(' ')[0].toLowerCase()),
  )
  if (!amountMatch || !beneficiary) return null

  const amountValue = Number(amountMatch[1]) * (amountMatch[2] ? 1000 : 1)
  if (!amountValue) return null

  const noteMatch = cleaned.match(/\bfor\s+([\w\s]{2,30})/i)
  return { beneficiary, amountValue, narration: noteMatch ? noteMatch[1].trim() : '' }
}

/** Builds the query string carried forward through draft, review and success. */
export function paymentQuery(payment: { party: string; account: string; bank: string; amountValue: number; narration: string }) {
  return new URLSearchParams({
    to: payment.party,
    acct: payment.account,
    bank: payment.bank,
    amt: String(payment.amountValue),
    note: payment.narration,
  }).toString()
}

/** Same contract for bill payments, so the biller and reference never drift. */
export function billQuery(payment: { bill: string | null; party: string; account: string; amountValue: number; narration: string }) {
  return new URLSearchParams({
    bill: payment.bill || '',
    prov: payment.party,
    target: payment.account,
    amt: String(payment.amountValue),
    plan: payment.narration,
  }).toString()
}
